/**
 * Boîte à outils — catalogue des outils pédagogiques et choix selon la question.
 *
 * Chaque outil déclare les niveaux où il sert (rangs de `schoolLevelRank`) et
 * les familles de domaines qu'il éclaire. Une question reliée à une SKILLCARD
 * donne un `domain_label` (texte libre en base, ~130 libellés) : on le ramène à
 * une famille par mots-clés, puis la boîte montre d'abord les outils de cette
 * famille, ensuite les autres outils du niveau.
 *
 * PUR : aucun DOM, aucune requête. Les icônes vivent dans l'UI.
 */

import { schoolLevelRank } from '@jeraisonne/db/cards';

export const TOOL_FAMILIES = [
  'calcul',
  'fractions',
  'arithmetique',
  'proportionnalite',
  'grandeurs',
  'geometrie',
  'transformations',
  'espace',
  'trigonometrie',
  'algebre',
  'fonctions',
  'suites',
  'integrales',
  'probabilites',
  'statistiques',
  'vecteurs',
  'complexes',
  'matrices',
  'algorithmique',
  'chimie',
  'physique',
] as const;
export type ToolFamily = (typeof TOOL_FAMILIES)[number];

export const TOOL_IDS = [
  'times-table',
  'pythagoras-squares',
  'thales',
  'right-triangle-trig',
  'line-sliders',
  'function-plotter',
  'trig-circle',
  'vector-sum',
  'dot-product',
  'sequences',
  'partial-sums',
  'pascal-triangle',
  'random-launcher',
  'bayes-tree',
  'galton-board',
  'stats-series',
  'percent-evolutions',
  'primes-grid',
  'place-value',
  'duration-clock',
  'number-machine',
  'coordinates-battleship',
  'root-square',
  'interval-builder',
  'number-sets',
  'moving-points',
  'reference-functions',
  'compound-e',
  'pi-polygon',
  'circle-equation',
  'chord-tangent',
  'riemann-sums',
  'urn-draws',
  'smart-calc',
  'balance',
  'algebra-tiles',
  'fraction-wall',
  'signed-counters',
  'proportion-table',
  'powers',
  'transformations',
  'angles',
  'modular-clock',
  'taylor',
  'complex-plane',
  'markov-graph',
  'matrix-calc',
  'logic',
  'python-sandbox',
  'unit-converter',
  'geoboard',
  'grid-robot',
  'solid-nets',
  'solid-sections',
  'number-line',
  'mean-leveling',
  'area-multiplication',
  'ruler-compass',
  'graphic-solve',
  'samples',
  'parabola',
  'tangent',
  'lottery-wheel',
  'concentration',
  'bisection',
  'direction-field',
  'slide-rule',
  'linear-map',
  'redox',
  'circuit',
  'motion',
  'waves',
  'optics',
  'atom',
  'ph',
  'matter',
  'gas',
  'colors',
  'rc',
  'law-sim',
  'reaction-progress',
  'gram-schmidt',
  'lagrange',
  'level-gradient',
  'param-curve',
  'space-frame',
] as const;
export type ToolId = (typeof TOOL_IDS)[number];

export interface ToolDef {
  id: ToolId;
  title: string;
  /** une ligne : ce que l'élève y fait */
  pitch: string;
  /** rangs de niveau inclus (6e = 1 … Tle = 7, MPSI/PTSI = 8, MP/PT = 9) */
  minRank: number;
  maxRank: number;
  families: readonly ToolFamily[];
  /**
   * Ce qui, dans une SKILLCARD, désigne CET outil (« Théorème de Pythagore »
   * → Pythagore en carrés, pas tous les outils de géométrie). Sert à la boîte
   * d'une question ET aux suggestions de liens `card_tools`.
   */
  match?: ToolMatch;
  /**
   * Outil propre à UNE notion (lanceur, arbre de Bayes, Galton) : mis en
   * avant seulement si la carte le désigne (`match`), jamais par la seule
   * famille de son domaine (« Loi exponentielle » n'appelle pas Galton).
   */
  notionOnly?: boolean;
  /**
   * Outil d'une autre matière que les maths (physique-chimie) : il ne
   * s'affiche que dans le contexte de sa famille (ou relié à la carte),
   * jamais dans les « Autres outils » d'une question de maths.
   */
  discipline?: 'physique-chimie';
}

/**
 * Règle de correspondance carte → outil, sur textes normalisés (minuscules,
 * sans accents, apostrophes et tirets en espaces). La carte correspond si son
 * titre ou son sujet vérifie `text`, ou son domaine `domain` — sauf si son
 * domaine vérifie `notDomain` (« Suites de fonctions » n'est pas pour l'outil
 * Suites) ou son titre / sujet `notText` (« Système de deux équations » n'est
 * pas pour la balance).
 */
export interface ToolMatch {
  text?: RegExp;
  domain?: RegExp;
  notDomain?: RegExp;
  notText?: RegExp;
}

/** Ce qu'on lit d'une SKILLCARD pour choisir ses outils. */
export interface CardTexts {
  domain_label?: string | null;
  topic?: string | null;
  title?: string | null;
}

export const TOOLS: readonly ToolDef[] = [
  {
    id: 'times-table',
    title: 'Table de Pythagore',
    pitch: 'Touche une case : le produit apparaît en rectangle. Défi : 20 produits.',
    minRank: 1,
    maxRank: 4,
    families: ['calcul', 'fractions', 'arithmetique', 'proportionnalite'],
    match: { text: /tables? de multiplication|multiplication par \d|tables? de \d/ },
  },
  {
    id: 'pythagoras-squares',
    title: 'Pythagore en carrés',
    pitch: 'Déforme le triangle : les carrés posés sur ses côtés et leurs aires suivent.',
    minRank: 3,
    maxRank: 5,
    families: ['geometrie'],
    match: { text: /pythagor/ },
  },
  {
    id: 'thales',
    title: 'Thalès glissant',
    pitch: 'Fais glisser la parallèle : les trois rapports restent égaux.',
    minRank: 3,
    maxRank: 5,
    families: ['geometrie'],
    match: { text: /thales/ },
  },
  {
    id: 'right-triangle-trig',
    title: 'Triangle SOH-CAH-TOA',
    pitch: 'Choisis l’angle : opposé, adjacent, hypoténuse et leurs rapports.',
    minRank: 4,
    maxRank: 5,
    families: ['trigonometrie'],
    match: { text: /cosinus|sinus|\bsoh\b|trigonometr/, domain: /trigonometr/ },
  },
  {
    id: 'line-sliders',
    title: 'Droite à curseurs',
    pitch: 'Règle a et b : la droite y = ax + b bouge, lis images et antécédents.',
    minRank: 4,
    maxRank: 5,
    families: ['fonctions', 'proportionnalite'],
    match: {
      text: /fonctions? affines?|fonctions? lineaires?|coefficient directeur|ordonnee a l origine|lire a et b|equation reduite|pente d une droite/,
      domain: /^droites?$/,
    },
  },
  {
    id: 'function-plotter',
    title: 'Traceur de courbes',
    pitch: 'Écris f(x) avec a, b ou c, puis fais bouger les curseurs.',
    minRank: 4,
    maxRank: 9,
    families: ['fonctions', 'algebre', 'trigonometrie'],
    match: {
      text: /courbe representative|lecture graphique|parabole|fonctions? de reference|image et antecedent|position relative de deux courbes|tableau de variations/,
      domain:
        /^(fonctions?|second degre|fonction exponentielle|exponentielle|logarithme|fonction logarithme|derivation|convexite|limites et continuite|continuite)$/,
      notDomain: /suites? de fonctions|series/,
    },
  },
  {
    id: 'trig-circle',
    title: 'Cercle trigonométrique',
    pitch: 'Fais tourner l’angle : cosinus, sinus et tangente, valeurs exactes comprises.',
    minRank: 5,
    maxRank: 9,
    families: ['trigonometrie', 'complexes'],
    match: { text: /cercle trigo|radian|trigonometr|\bcos x\b|\bsin x\b/, domain: /trigonometr/ },
  },
  {
    id: 'vector-sum',
    title: 'Relation de Chasles',
    pitch: 'Mets les vecteurs bout à bout : AB + BC = AC, coordonnées comprises.',
    minRank: 5,
    maxRank: 6,
    families: ['vecteurs', 'geometrie'],
    match: {
      text: /chasles|colineai|somme de vecteurs|vecteurs? egaux|egalite de vecteurs/,
      domain: /^vecteurs?$/,
    },
  },
  {
    id: 'dot-product',
    title: 'Produit scalaire',
    pitch: 'Tourne deux vecteurs : le projeté donne u · v, nul à angle droit.',
    minRank: 6,
    maxRank: 9,
    families: ['vecteurs'],
    match: {
      text: /produit scalaire|al kashi|projete orthogonal|projection orthogonale/,
      domain: /^produit scalaire$/,
    },
  },
  {
    id: 'sequences',
    title: 'Suites',
    pitch: 'Arithmétiques, géométriques, uₙ₊₁ = f(uₙ) : nuage, toile d’araignée, limite.',
    minRank: 6,
    maxRank: 9,
    families: ['suites'],
    match: {
      text: /\bsuites?\b|recurren|arithmetico|point fixe/,
      domain: /^suites?$/,
      notDomain: /fonctions|matrice|serie|graphe/,
    },
  },
  {
    id: 'partial-sums',
    title: 'Sommes partielles',
    pitch: 'Σ 1/n contre Σ 1/n², série alternée : regarde Sₙ converger… ou non.',
    minRank: 8,
    maxRank: 9,
    families: ['suites'],
    match: {
      text: /series? de riemann|series? alternees?|serie harmonique|sommes? partielles?|serie geometrique|serie exponentielle|comparaison serie integrale/,
      domain: /^series( numeriques)?$/,
    },
  },
  {
    id: 'pascal-triangle',
    title: 'Triangle de Pascal',
    pitch: 'Touche une case : elle est la somme des deux du dessus.',
    minRank: 7,
    maxRank: 9,
    families: ['probabilites'],
    match: {
      text: /pascal|binom|\bparmi\b|combinaisons?\b(?! lineaires?)|arrangements?|denombr/,
      domain: /^denombrement$/,
    },
  },
  {
    id: 'random-launcher',
    title: 'Lanceur',
    pitch: 'Pièce, dé, roue : lance 10 000 fois, la fréquence rejoint la probabilité.',
    minRank: 1,
    maxRank: 6,
    families: ['probabilites'],
    notionOnly: true,
    match: {
      text: /hasard|equiprobab|chances sur|simulation|stabilisation des frequences|frequence et probabilite|fluctuation|echantillon|evenement contraire|grands nombres|de equilibre|pile ou face/,
      notDomain: /suites?|algorithm|informatique/,
    },
  },
  {
    id: 'bayes-tree',
    title: 'Arbre et carré de Bayes',
    pitch: 'Arbre pondéré, carré découpé, tableau croisé : inverse une condition.',
    minRank: 3,
    maxRank: 9,
    families: ['probabilites'],
    notionOnly: true,
    match: {
      text: /conditionnel|bayes|probabilites totales|arbre|tableau croise|double entree|depistage|faux positifs|evenements independants|independance de deux evenements|deux epreuves|epreuves successives|(avec|sans) remise/,
      notDomain: /graphe|matrice|algorithm|informatique|series?|suites?/,
    },
  },
  {
    id: 'galton-board',
    title: 'Planche de Galton',
    pitch: 'Lâche des billes : l’histogramme du nombre de succès se dessine.',
    minRank: 6,
    maxRank: 9,
    families: ['probabilites'],
    notionOnly: true,
    match: {
      text: /binomiale|bernoulli|galton|bienayme|tchebychev|concentration|grands nombres|somme de variables aleatoires|epreuves independantes/,
      notDomain: /chimie|physique/,
      notText: /molaire|quantite de matiere|dosage|titrage|dilution/,
    },
  },
  {
    id: 'stats-series',
    title: 'Série statistique',
    pitch: 'Fais glisser des valeurs : moyenne, médiane, quartiles et écart type suivent.',
    minRank: 2,
    maxRank: 6,
    families: ['statistiques'],
    match: {
      text: /(?<!vitesse |valeur )moyenne|mediane|etendue|quartile|boite a moustaches|ecart type|interquartile|dispersion|serie statistique|diagramme en (barres|batons)/,
      notDomain: /probabilit|integr|fonction|grandeur|suite|geometr|triangle/,
    },
  },
  {
    id: 'percent-evolutions',
    title: 'Pourcentages et évolutions',
    pitch:
      'Hausses et baisses en chaîne : coefficient multiplicateur, taux global, taux réciproque.',
    // avant la 3e, l'outil n'ouvre que « pourcentage d'une quantité »
    minRank: 1,
    maxRank: 6,
    families: ['proportionnalite'],
    match: {
      text: /pourcentage|evolutions? successives|coefficient multiplicateur|taux d evolution(?! moyen)|taux reciproque|proportion d une proportion|information chiffree/,
      domain: /information chiffree/,
      notDomain: /suites?|fonctions?|exponentielle/,
    },
  },
  {
    id: 'primes-grid',
    title: 'Multiples et nombres premiers',
    pitch: 'Grille de 1 à 100, crible d’Ératosthène, décomposition et PGCD.',
    minRank: 1,
    maxRank: 7,
    families: ['arithmetique'],
    match: {
      text: /multiples?\b|\bdiviseurs?\b(?! decimal)|nombres? premiers?|crible|eratosthene|divisibilite|facteurs premiers|pgcd|ppcm|euclide|fractions? irreductibles?|simplifier une fraction|premiers entre eux/,
      notDomain: /polynome|matrice|graphe/,
    },
  },
  {
    id: 'place-value',
    title: 'Tableau de numération',
    pitch: '× 10, ÷ 100 : les chiffres glissent, la virgule reste.',
    minRank: 1,
    maxRank: 3,
    families: ['calcul', 'fractions'],
    notionOnly: true,
    match: {
      text: /numeration|nombres? decimaux|(multiplier|diviser|multiplication|division) par (10|100|1 ?000|0,0?0?1)\b|par 10 ?,? 100|valeur positionnelle|chiffre des|fractions? decimales?|dixiemes?|centiemes?|milliemes?|ecriture decimale/,
      notDomain: /fonction|suite|probab|statist/,
    },
  },
  {
    id: 'duration-clock',
    title: 'Horloge des durées',
    pitch: 'Fais tourner les aiguilles : durée entre deux heures, 1 h = 60 min.',
    minRank: 1,
    maxRank: 2,
    families: ['grandeurs'],
    notionOnly: true,
    match: {
      text: /\bdurees?\b|horaires?|heure d (arrivee|depart)|heures? et (des )?minutes|convertir des durees/,
      notDomain: /physique|vitesse|fonction|suite/,
    },
  },
  {
    id: 'number-machine',
    title: 'Machine à nombres',
    pitch: 'Un programme de calcul en étapes ; remplace le nombre de départ par x.',
    minRank: 1,
    maxRank: 4,
    families: ['algebre'],
    notionOnly: true,
    match: {
      text: /programmes? de calcul|nombre de depart|par substitution|valeur d une expression|demontrer avec le calcul litteral|preuve generale par les lettres/,
      notDomain: /systeme/,
    },
  },
  {
    id: 'coordinates-battleship',
    title: 'Bataille navale du repère',
    pitch: 'Placer, lire, viser : abscisse puis ordonnée, en jouant.',
    minRank: 1,
    maxRank: 3,
    families: ['geometrie'],
    notionOnly: true,
    match: {
      text: /lire et placer|placer (un|des) points?|reperage dans le plan|repere du plan|coordonnees d un point|abscisse et ordonnee/,
      notDomain: /espace|vecteur|differentiel|bilineaire|produit scalaire/,
    },
  },
  {
    id: 'root-square',
    title: 'Carré dont on règle l’aire',
    pitch: 'Le côté vaut √aire : encadre-le entre deux entiers, puis au dixième.',
    minRank: 3,
    maxRank: 5,
    families: ['calcul'],
    notionOnly: true,
    match: {
      text: /racines? carrees?|carres? parfaits?|encadrer une racine|irrationn|x ?² ?= ?a\b/,
      notDomain: /fonction|complexe|polynome|second degre/,
    },
  },
  {
    id: 'interval-builder',
    title: 'Constructeur d’intervalles',
    pitch: 'Crochets ouverts ou fermés, réunion, intersection, |x − a| ≤ r.',
    minRank: 5,
    maxRank: 6,
    families: ['algebre'],
    notionOnly: true,
    match: {
      text: /intervalles?|inequations? du premier degre|reunion|valeur absolue/,
      notDomain:
        /trigo|derivation|exponentielle|logarithme|suite|integr|probab|fonction|statist|droite|geometr/,
    },
  },
  {
    id: 'number-sets',
    title: 'Ensembles de nombres',
    pitch: 'Range 7, −3, √9, 2/3, √2, π… dans le plus petit ensemble : ℕ ⊂ ℤ ⊂ 𝔻 ⊂ ℚ ⊂ ℝ.',
    minRank: 5,
    maxRank: 6,
    families: ['calcul'],
    notionOnly: true,
    match: {
      text: /ensembles? de nombres|irrationn|nombres? decimaux et rationnels|appartenance|notations ensemblistes/,
      notDomain: /fonction|probab|complexe|espace|geometr|droite/,
    },
  },
  {
    id: 'moving-points',
    title: 'Points mobiles',
    pitch: 'Déplace A et B : milieu et longueur, avec les formules remplies.',
    minRank: 5,
    maxRank: 6,
    families: ['geometrie'],
    notionOnly: true,
    match: {
      text: /milieu d un segment|milieu et distance|distance (entre )?deux points|coordonnees du milieu|longueur d un segment/,
      notDomain: /espace|complexe/,
    },
  },
  {
    id: 'reference-functions',
    title: 'Fonctions de référence',
    pitch:
      'x², 1/x, √x, x³, |x| : un point court sur la courbe et remplit le tableau de variations.',
    minRank: 5,
    maxRank: 6,
    families: ['fonctions'],
    notionOnly: true,
    match: {
      text: /fonctions? de reference|fonction (carre|inverse|cube|racine carree|valeur absolue)|parite|fonctions? paires?|impaires?|tableau de variations|variations, tableau|ensemble de definition/,
      notDomain:
        /suite|exponentielle|logarithme|derivation|integr|trigo|arithmet|raisonnement|calcul litteral/,
    },
  },
  {
    id: 'compound-e',
    title: 'Des intérêts composés à e',
    pitch: '(1 + 1/n)ⁿ monte vers e ; pour eᵏˣ, la pente vaut k fois la hauteur.',
    minRank: 6,
    maxRank: 7,
    families: ['fonctions'],
    notionOnly: true,
    match: {
      text: /interets composes|nombre e\b|croissance exponentielle|exponentielle : definition|e\^\(?k?a?x\)?/,
      notDomain: /complexe|probab|primitive|integr|logarithme|derivation/,
    },
  },
  {
    id: 'pi-polygon',
    title: 'Archimède : approcher π',
    pitch:
      'Un polygone régulier dans un cercle de rayon 1 : de 3 à 1000 côtés, son périmètre s’approche de 2π et les décimales de π apparaissent.',
    minRank: 1,
    maxRank: 9,
    families: ['geometrie'],
    notionOnly: true,
    match: {
      text: /nombre pi\b|nombre π|le nombre π|d ou vient (le nombre )?(pi|π)|approch\w* (de )?(pi|π)|(methode|polygones?) d archimede|perimetre (du|d un) cercle|circonference/,
      notText: /trigonometri|radian|cercle trigonometrique|aire d un disque/,
    },
  },
  {
    id: 'circle-equation',
    title: 'Cercle et équation',
    pitch: 'Centre et rayon au doigt : (x − a)² + (y − b)² = r², forme développée, diamètre [AB].',
    minRank: 6,
    maxRank: 7,
    families: ['geometrie'],
    notionOnly: true,
    match: {
      text: /equations? (d un |de |du )?cercle|cercle de diametre|centre et rayon|ma ?· ?mb ?= ?0/,
      notDomain: /trigo|espace|complexe/,
    },
  },
  {
    id: 'chord-tangent',
    title: 'Corde et tangente',
    pitch: 'Convexe : corde au-dessus, tangentes en dessous ; le point d’inflexion change tout.',
    minRank: 7,
    maxRank: 8,
    families: ['fonctions'],
    notionOnly: true,
    match: {
      text: /convex|concav|inflexion|derivee seconde/,
      notDomain: /probab|suite|complexe|espace/,
    },
  },
  {
    id: 'riemann-sums',
    title: 'Rectangles contre trapèzes',
    pitch: 'L’aire sous la courbe par n rectangles ou trapèzes, contre l’intégrale exacte.',
    minRank: 7,
    maxRank: 9,
    families: ['integrales'],
    notionOnly: true,
    match: {
      text: /sommes? de riemann|integrale et aire|aire sous (la )?courbe|methode des (rectangles|trapezes)|trapezes|approximation d (une )?integrale/,
      notDomain: /serie|probab|suite/,
    },
  },
  {
    id: 'urn-draws',
    title: 'Urne et tirages',
    pitch: 'Boules de couleur, avec ou sans remise : l’arbre se construit, les fractions changent.',
    minRank: 3,
    maxRank: 9,
    families: ['probabilites'],
    notionOnly: true,
    match: {
      text: /\burnes?\b|tirages? (successifs|simultanes?)|(avec|sans) remise|epreuves successives|deux epreuves|k uplets|\barrangements?\b|hypergeometrique|loi de probabilite|probabilite conditionnelle|evenements independants|independance de deux evenements/,
      notDomain: /graphe|matrice|algorithm|informatique|series?|suites?/,
    },
  },
  {
    id: 'smart-calc',
    title: 'Calcul malin',
    pitch: 'Arrondir, compenser, découper : 39 + 52, 49 × 19 ou 51² de tête.',
    minRank: 1,
    maxRank: 5,
    families: ['calcul', 'algebre'],
    notionOnly: true,
    match: {
      text: /calcul mental|calculer mentalement|calcul (rapide|reflechi|astucieux)|calculer astucieusement|de tete|multiplier par (5|25|50|9|11|99)\b|distributivite|developp(er|ement)|identites? remarquables?|produits? remarquables?|carre d une (somme|difference)|difference de (deux )?carres/,
      notDomain:
        /fonction|suite|probab|statist|matri|vecteur|complexe|fraction|relatif|puissance|grandeur|decima|raisonnement|logique/,
    },
  },
  {
    id: 'balance',
    title: 'Balance des équations',
    pitch: 'Le même geste sur les deux plateaux, jusqu’à x = … ; pour une inéquation, elle penche.',
    minRank: 2,
    maxRank: 5,
    families: ['algebre'],
    notionOnly: true,
    match: {
      text: /(in)?equations? du (1er|premier) degre|resoudre (une |l |des |cette )?(in)?equations?(?! (produit|quotient|du (second|2nd|2e) degre|de (la )?droite|differentielle|cartesienne))|resolution d (in)?equations?|resoudre (ax|x \+)|equations? du type (ax|x \+)|(inconnue|x) des deux (cotes|membres)|(mettre|mise) (un probleme )?en equation|operations inverses|(tester|verifier) (si )?(un nombre|une valeur)( est)? solution/,
      notDomain:
        /trigo|exponentiel|logarithm|fonction|droite|cercle|differenti|complexe|matric|systeme|second degre/,
      notText: /systeme|second degre|2nd degre|x2|congru|fraction/,
    },
  },
  {
    id: 'algebra-tiles',
    title: 'Tuiles algébriques',
    pitch: '(x + 3)(x + 2) devient un rectangle de tuiles : développer, factoriser, identités.',
    minRank: 2,
    maxRank: 5,
    families: ['algebre'],
    notionOnly: true,
    match: {
      text: /developp(er|ement)(?! (decimal|limite))|factoris(?!\w* (un entier|en (produit de )?facteurs premiers|un polynome|un trinome))|mettre en facteur|identites? remarquables?|distributivite|produit de deux sommes|difference de (deux )?carres|carre d une (somme|difference)|\(a ?[+-] ?b\) ?2/,
      notDomain:
        /fonction|suite|probab|statist|matri|vecteur|complexe|fraction|relatif|puissance|grandeur|decima|raisonnement|logique|arithmetique|nombres premiers/,
      // distributivité sur des nombres (Calcul malin) ; factoriser 3x + x avant la 4e : pas de mode Factoriser
      notText: /mentalement|calcul mental|sur des nombres|neutre/,
    },
  },
  {
    id: 'fraction-wall',
    title: 'Mur des fractions',
    pitch:
      'Des barres partagées jusqu’aux douzièmes : fractions égales, comparer, ajouter, multiplier.',
    minRank: 1,
    maxRank: 4,
    families: ['fractions'],
    match: {
      text: /\bfractions?\b(?! (rationnelles?|continues?))|nombres? rationnels?|inverse d un nombre/,
      domain: /^fractions?$/,
      notDomain:
        /arithmetique|probab|statist|fonction|matri|complexe|equation|algebre lineaire|proportion/,
      notText: /equation|facteurs premiers|puissance|developpement decimal|pourcentage/,
    },
  },
  {
    id: 'signed-counters',
    title: 'Jetons + et −',
    pitch: 'Une paire (+1 ; −1) vaut 0 : additionner, soustraire, multiplier des relatifs.',
    minRank: 2,
    maxRank: 4,
    families: ['calcul'],
    notionOnly: true,
    match: {
      text: /(nombres?|entiers?) relatifs?|\brelatifs\b|regle des signes|soustraire c est ajouter l oppose|nombres? negatifs?/,
      domain: /relatif/,
      notDomain: /fonction|equation|vecteur|geometrie|algebre/,
      notText: /vecteur|valeur absolue/,
    },
  },
  {
    id: 'proportion-table',
    title: 'Tableau de proportionnalité',
    pitch:
      'Quotients et coefficient, droite par l’origine, valeur manquante de quatre façons, partage selon un ratio.',
    minRank: 1,
    maxRank: 4,
    families: ['proportionnalite'],
    notionOnly: true,
    match: {
      text: /proportionnel|proportionnalite|quatrieme proportionnelle|retour a l unite|produit en croix|\bratios?\b|partage proportionnel|linearite/,
      domain: /^proportionnalite$/,
      notDomain: /fonction|probab|statist|integr|algebre lineaire|suite/,
      notText:
        /pourcentage|evolution|proportion d une proportion|fonction lineaire|integrale|application lineaire/,
    },
  },
  {
    id: 'powers',
    title: 'Puissances',
    pitch:
      'aⁿ en facteurs, l’échelle de l’atome aux galaxies (× 10 à chaque cran), l’écriture scientifique, les règles.',
    minRank: 2,
    maxRank: 5,
    families: ['calcul'],
    notionOnly: true,
    match: {
      text: /\bpuissances?\b|notation scientifique|ecriture scientifique|exposants?\b|carre et cube|prefixes/,
      domain: /^puissances?$/,
      notDomain:
        /matri|complex|suite|fonction|logarithm|arithmetique|reduction|algebre lineaire|congru/,
      notText: /matrice|modulo|moivre|serie|congru|fonction|croissances comparees|controler/,
    },
  },
  {
    id: 'transformations',
    title: 'Transformations sur quadrillage',
    pitch:
      'Symétrie axiale, demi-tour, translation : observer l’image, puis la construire au doigt.',
    minRank: 1,
    maxRank: 4,
    families: ['transformations'],
    match: {
      text: /symetri|demi tour|translation|transformations?\b|frises?|pavages?/,
      notDomain:
        /fonction|complexe|matri|algebre lineaire|bilineaire|probab|statist|suite|physique|chimie|energie|matiere/,
      notText:
        /rotation|homothetie|isometrie|parite|fonction|somme de vecteurs|chasles|energie|chimique|etat|expression/,
    },
  },
  {
    id: 'angles',
    title: 'Angles',
    pitch: 'Rapporteur à double graduation, somme des angles d’un triangle, angles et parallèles.',
    minRank: 1,
    maxRank: 4,
    families: ['geometrie'],
    notionOnly: true,
    match: {
      text: /\bangles?\b|rapporteur|bissectrice|alternes? internes?|angles? correspondants?/,
      notDomain: /trigo|fonction|complexe|vecteur|algebre|produit scalaire/,
      notText:
        /oriente|cercle trigo|radian|produit scalaire|trigonometr|cosinus|sinus|tangente|inscrit|au centre|vecteur/,
    },
  },
  {
    id: 'modular-clock',
    title: 'Horloge modulaire',
    pitch: 'Un cadran à n heures : restes, multiples et puissances modulo n.',
    minRank: 7,
    maxRank: 9,
    families: ['arithmetique'],
    match: { text: /congru|modulo|fermat|division euclidienne|restes? modulo|tableau de restes/ },
  },
  {
    id: 'taylor',
    title: 'Polynômes de Taylor',
    pitch: 'Monte l’ordre n : le polynôme épouse la courbe près de 0.',
    minRank: 8,
    maxRank: 9,
    families: ['fonctions'],
    match: {
      text: /taylor|developpements? limites?|\bdl\b/,
      domain: /^analyse asymptotique$/,
    },
  },
  {
    id: 'complex-plane',
    title: 'Plan complexe',
    pitch: 'Déplace z : module, argument, produit qui tourne, racines en polygone.',
    minRank: 7,
    maxRank: 9,
    families: ['complexes'],
    match: {
      text: /nombres? complexes?|plan complexe|racines complexes|affixe|argand|moivre|formule d euler|racines n iemes de l unite|similitudes? directes?/,
      domain: /^nombres complexes$/,
      notDomain: /chimie|physique/,
    },
  },
  {
    id: 'markov-graph',
    title: 'Graphes et Markov',
    pitch: 'Des états et des flèches pondérées : πₙ = π₀Pⁿ converge, Mᵏ compte les chemins.',
    minRank: 7,
    maxRank: 7,
    families: ['matrices'],
    notionOnly: true,
    match: {
      text: /markov|graphe probabiliste|graphe pondere|matrice d adjacence|matrice de transition|etat stable|distribution invariante|nombre de chemins/,
      domain: /^(graphes et matrices|matrices et graphes)$/,
      notText: /inegalite|calcul matriciel|inverse|produit de deux matrices|systeme lineaire/,
    },
  },
  {
    id: 'matrix-calc',
    title: 'Calcul matriciel',
    pitch: 'Ligne × colonne case par case, inverse, système AX = Y, puissances Aⁿ.',
    minRank: 7,
    maxRank: 8,
    families: ['matrices'],
    // en prépa, l'algèbre linéaire dépasse les matrices 2 × 2 : seulement si la carte le désigne
    notionOnly: true,
    match: {
      text: /produit de deux matrices|produit matriciel|calcul matriciel|inverse d une matrice|matrices? inversibles?|systemes? lineaires?|puissances? de matrices|suites couplees/,
      domain: /^matrices$/,
      notText: /pivot|par blocs/,
    },
  },
  {
    id: 'logic',
    title: 'Logique et ensembles',
    pitch: 'Venn, tables de vérité, ∀ et ∃ : contre-exemple, contraposée, négation.',
    minRank: 5,
    maxRank: 9,
    families: ['algorithmique'],
    notionOnly: true,
    match: {
      text: /logique|ensembliste|quantificat|contraposee|conditions? (necessaire|suffisante)|implication|equivalence|contre exemple|par l absurde|negation|tables? de verite|venn/,
      domain: /^(logique|raisonnement)/,
      notDomain: /logarithme|exponentielle|fonction|suite|analyse|topologie|differentiel/,
      notText:
        /recurrence|injection|surjection|bijection|image directe|analyse synthese|irrationnel|2n|disjonction|ensembles? de nombres|programmes?/,
    },
  },
  {
    id: 'python-sandbox',
    title: 'Bac à sable Python',
    pitch: 'Écris un programme, exécute-le, puis suis-le ligne par ligne.',
    minRank: 3,
    maxRank: 9,
    families: ['algorithmique'],
    notionOnly: true,
    match: {
      text: /python|algorithm|boucles?|instructions? conditionnelles?|dichotomie|balayage/,
      domain: /^algorithmique$/,
      notText: /programmes? de calcul/,
    },
  },
  {
    id: 'unit-converter',
    title: 'Tableau de conversion',
    pitch: 'Pose le nombre, déplace la virgule : longueurs, masses, aires, volumes, litres.',
    minRank: 1,
    maxRank: 5,
    families: ['grandeurs'],
    notionOnly: true,
    match: {
      text: /convers|convertir|unites? de (longueur|masse|volume|capacite|contenance)|unites? d aire|contenances?|grandeurs? quotients?|vitesse moyenne/,
      notDomain: /trigo|nombre|fraction|probab|calcul|algebre|fonction|decima/,
      notText: /\bdurees?\b|horaires?|\bangles?\b|radian|fraction|decimal/,
    },
  },
  {
    id: 'geoboard',
    title: 'Géoplan : aires et périmètres',
    pitch: 'Pose des sommets sur les clous : aire en carreaux, périmètre, formules qui bougent.',
    minRank: 1,
    maxRank: 3,
    families: ['grandeurs', 'geometrie'],
    notionOnly: true,
    match: {
      text: /\baires?\b|perimetre|geoplan/,
      notDomain: /calcul|algebre|fonction|probab/,
      notText:
        /aire entre|aire sous|integr|densite|sphere|boule|cylindre|cone|pyramide|pave|prisme|volume|laterale/,
    },
  },
  {
    id: 'grid-robot',
    title: 'Robot sur quadrillage',
    pitch: 'Avancer, tourner, répéter : programme le robot, ou prévois où il s’arrête.',
    minRank: 1,
    maxRank: 3,
    families: ['algorithmique'],
    notionOnly: true,
    match: {
      text: /pensee informatique|robot|repeter|scratch|deplacements? (absolus?|relatifs?)/,
      domain: /^pensee informatique$/,
      notDomain: /probab|statist/,
      notText: /tant que|python|experience aleatoire/,
    },
  },
  {
    id: 'solid-nets',
    title: 'Patrons qui se plient',
    pitch: 'Un patron se replie en solide sous tes yeux ; trouve les 11 patrons du cube.',
    minRank: 1,
    maxRank: 3,
    families: ['espace'],
    notionOnly: true,
    match: {
      text: /\bpatrons?\b|solides? usuels?|(reconnaitre|nommer|decrire) .*solides?|vision dans l espace/,
      notDomain: /fonction|probab|statist|algebre|suite/,
      notText: /sections?|sphere|boule|repere|vecteur|equation|droite|plan /,
    },
  },
  {
    id: 'solid-sections',
    title: 'Sections de solides',
    pitch:
      'Un plan glisse dans un pavé, un cylindre, une pyramide, un cône ou une boule : quelle section ?',
    minRank: 4,
    maxRank: 5,
    families: ['espace'],
    notionOnly: true,
    match: {
      text: /\bsections? (planes?|(d une?|du|de la|de l|des?) (solides?|cube|pave|prisme|cylindre|pyramide|cone|sphere|boule))|sphere et boule|grands? cercles?|volume d une pyramide et d un cone/,
      notDomain: /fonction|probab|statist|suite|algebre bilineaire|geometrie differentielle/,
      notText: /equation|repere|vecteur|parametr/,
    },
  },
  {
    id: 'number-line',
    title: 'Droite graduée zoomable',
    pitch: 'Zoome de l’unité au millième : placer, encadrer, arrondir, comparer.',
    minRank: 1,
    maxRank: 5,
    families: ['calcul'],
    notionOnly: true,
    match: {
      text: /droite graduee|abscisses?|comparer (deux )?(des )?(nombres )?(decimaux|relatifs)|ranger des (nombres )?decimaux|arrondir|valeurs? (approchees?|arrondies?)|troncature|encadrer (un|des) (nombres? )?decima|intercaler/,
      notDomain: /fonction|suite|probab|statist|geometr|vecteur|integr/,
      // « dans un repère » exclu, mais pas « repérer sur une droite graduée »
      notText:
        /racine|√|intervalle|fraction|\brepere\b|coordonnees|courbe|curviligne|ordonnee|origine|balayage/,
    },
  },
  {
    id: 'mean-leveling',
    title: 'Moyenne qui nivelle',
    pitch:
      'Ce qui dépasse comble les creux : la moyenne, simple ou pondérée ; diagrammes en barres et circulaire.',
    minRank: 1,
    maxRank: 5,
    families: ['statistiques'],
    notionOnly: true,
    match: {
      text: /calculer (une|la) moyenne|moyenne (simple|ponderee|d une serie)|diagrammes? (en batons|circulaires?|en barres)|(lire|construire) un tableau ou un diagramme|effectifs? (et|ou) (des? |une )?frequences?|calculer (des|une) frequences?/,
      notDomain: /probab|fonction|grandeur|suite|integr/,
      notText: /vitesse|valeur moyenne|ecart type|quartile|boite|linearite|cumul/,
    },
  },
  {
    id: 'area-multiplication',
    title: 'Multiplication en rectangle',
    pitch:
      '23 × 14, c’est l’aire d’un rectangle découpé : produits partiels et multiplication posée.',
    minRank: 1,
    maxRank: 2,
    families: ['calcul'],
    notionOnly: true,
    match: {
      text: /multipli(er|cation) .*decima|produit .*decimaux|multiplication posee|produits? partiels?|distributivite pour calculer|ordre de grandeur (d un |du )?produit|controler avec un ordre de grandeur/,
      notDomain: /fonction|suite|probab|statist|algebre|fraction|relatif|puissance|grandeur/,
      notText: /par 10\b|par 100\b|par 1 ?000|par 0,|tables? de|somme|difference|quotient|division/,
    },
  },
  {
    id: 'ruler-compass',
    title: 'Règle, équerre, compas',
    pitch: 'Construis à la règle, à l’équerre et au compas ; déplace un point, la figure suit.',
    minRank: 1,
    maxRank: 4,
    families: ['geometrie'],
    notionOnly: true,
    match: {
      // « construire » seulement suivi d'un objet de géométrie plane
      text: /mediatrices?|construi\w* (?:(?:un|une|le|la|les|des|d un|l) )?(?:triangles?|cercles?|droites?|segments?|parallelogrammes?|losanges?|rectangles?|carres?|figures?|mediatrices?|perpendiculaires?|paralleles?|hauteurs?|symetriques?|points?)|constructions? (?:de|d un|d une|du|des|geometrique)|compas|equerre|perpendiculaires?|droites? paralleles?|\bcercles?\b|hauteurs?|\bcorde\b|parallelogrammes?|losanges?|triangles? particuliers?|isoceles?|equilateral|distance d un point a une droite/,
      notDomain:
        /fonction|probab|statist|donnees|suite|vecteur|espace|grandeur|algebre|calcul|proportion|informatique|raisonnement|logique|nombre|arithmetique|fraction|repere/,
      notText:
        /demi tour|translation|rotation|homothetie|\baires?\b|perimetre|\bangles?\b|repere|equation|pythagore|thales|trigo|milieux|patron|solide|pyramide|\bcones?\b|cylindre|graphique|disque|coefficients? directeurs?|produit scalaire/,
    },
  },
  {
    id: 'graphic-solve',
    title: 'Résolution graphique',
    pitch:
      'Deux courbes, ou une courbe et y = k : la zone où f(x) ≥ g(x) se colore, l’ensemble des solutions s’écrit.',
    minRank: 4,
    maxRank: 6,
    families: ['fonctions'],
    notionOnly: true,
    match: {
      text: /resoudre graphiquement|resolution graphique|position (relative )?(de deux courbes|d une courbe|de la courbe)|lecture graphique|lire des solutions|antecedents?|f\(x\) ?(<|>|=|≤|≥|⩽|⩾) ?(g\(x\)|k\b|−?-?\d)|signe (d un|du) trinome|signe d un polynome du second degre|inequations? du second degre/,
      notDomain:
        /exponentielle|logarithme|integr|derivation|suite|probab|statist|trigo|geometr|proportion|limite|convexite|donnees|grandeur|reperage|vecteur|information|arithmetique|transformation/,
      notText:
        /tangente|asymptote|\baires?\b|courbure|parametree|convexite|\bcoefficients?\b|systemes?|derive|variations?|coordonnees/,
    },
  },
  {
    id: 'samples',
    title: 'Échantillons',
    pitch:
      '100 échantillons de taille n : les fréquences se resserrent autour de p ; environ 95 % ou plus dans p ± 1/√n.',
    minRank: 5,
    maxRank: 6,
    families: ['probabilites', 'statistiques'],
    notionOnly: true,
    match: {
      text: /echantillon|fluctuation|grands nombres|estimer une (probabilite|proportion)|estimation d une (probabilite|proportion)|frequence observee/,
      notDomain: /suite|algorithm|informatique/,
      notText: /moyenne d un echantillon|bienayme|tchebychev|concentration|variance|loi faible/,
    },
  },
  {
    id: 'parabola',
    title: 'Parabole en trois formes',
    pitch:
      'Développée, canonique, factorisée : règle a, b, c ou α, β ou les racines ; Δ, sommet et signe suivent.',
    minRank: 6,
    maxRank: 7,
    families: ['fonctions', 'algebre'],
    notionOnly: true,
    match: {
      text: /(second|2nd|2e) degre|trinomes?|paraboles?|forme canonique|discriminant|polynomes? de degre 2/,
      domain: /^second degre$/,
      notDomain: /suite|probab|statist|complexe|integr|geometr|derivation|mecanique|mouvement/,
      notText: /complexes?|ℂ|tangente/,
    },
  },
  {
    id: 'tangent',
    title: 'Tangente glissante',
    pitch:
      'A glisse sur la courbe : la tangente, sa pente f′(a), la sécante qui devient tangente, la courbe de f′ qui se trace.',
    minRank: 6,
    maxRank: 7,
    families: ['fonctions'],
    notionOnly: true,
    match: {
      text: /nombre derive|tangente|taux d accroissement|taux de variation|secante|derivation|derivee|derivable|fonction derivee/,
      notDomain:
        /suite|probab|statist|complexe|geometr|integr|trigo|primitive|equation differentielle|limite/,
      notText:
        /convex|concav|inflexion|derivee seconde|primitive|implicite|parametree|courbure|optimisation|d un produit|produit de deux|quotient|compose/,
    },
  },
  {
    id: 'lottery-wheel',
    title: 'Roue de loterie',
    pitch:
      'Règle les secteurs, les gains et la mise : loi, espérance en fractions, et le gain moyen qui rejoint E(X).',
    minRank: 6,
    maxRank: 7,
    families: ['probabilites'],
    notionOnly: true,
    match: {
      text: /variables? aleatoires?|esperance|jeu equitable|gain moyen|loi de probabilite|variance|ecart type/,
      notDomain: /suite|statist|geometr|complexe|integr/,
      notText:
        /binomiale|bernoulli|densite|continue|uniforme sur|loi normale|loi exponentielle|sommes? de (deux )?variables|d une somme|linearite de l esperance|echantillon|moyenne empirique|independantes?|concentration|tchebychev|grands nombres|geometrique/,
    },
  },
  {
    id: 'concentration',
    title: 'Bande de concentration',
    pitch:
      'Des moyennes d’échantillons, une bande autour de μ : la part qui en sort, contre le majorant V/(nδ²).',
    minRank: 7,
    maxRank: 9,
    families: ['probabilites'],
    notionOnly: true,
    match: {
      text: /concentration|bienayme|tchebychev|grands nombres|moyenne d un echantillon|variable aleatoire moyenne|moyenne empirique|sommes? de (deux |n )?variables aleatoires|echantillon de taille n/,
      notDomain: /suite|statist|geometr|graphe|matrice|chimie|physique|solution/,
      notText:
        /chaines? de markov|graphe|molaire|quantite de matiere|en masse|dosage|titrage|dilution/,
    },
  },
  {
    id: 'bisection',
    title: 'Dichotomie',
    pitch:
      'L’intervalle se coupe en deux à chaque étape : on garde la moitié où f change de signe, le programme Python suit.',
    minRank: 7,
    maxRank: 8,
    families: ['fonctions', 'algorithmique'],
    notionOnly: true,
    match: {
      text: /dichotomie|valeurs intermediaires|\btvi\b|theoreme de la bijection|balayage|encadrer une solution|(valeur|solution) approchee d une (solution|equation)|approximation d une solution|resolution approchee/,
      notDomain: /suite|probab|statist|geometr|complexe|differentiel|integr|primitive/,
      notText: /listes?|\btri(e|ee|es|ees)?\b|euler|extremum|maximum|minimum/,
    },
  },
  {
    id: 'direction-field',
    title: 'Champ de directions',
    pitch:
      'Les petits segments de pente y′ = ay + b ou y′ = f(x) ; touche un point, la solution qui y passe se trace avec sa formule.',
    minRank: 7,
    maxRank: 9,
    families: ['integrales'],
    notionOnly: true,
    match: {
      text: /equations? differentielles?|champ de directions|primitive verifiant|primitives? a une constante pres|condition initiale|y′ = ay/,
      domain: /equations? differentielles?/,
      notDomain: /suite|probab|statist|geometr|complexe|matrice/,
      notText:
        /ordre (2|deux)|2nd ordre|second ordre|y″|y′′|oscillat|harmonique|caracteristique|reconnaissance|integration par parties|changement de variable|series? entieres?|systemes?|wronskien|cauchy lipschitz|recherche d une solution particuliere|second membre|variation des constantes|d ordre n/,
    },
  },
  {
    id: 'slide-rule',
    title: 'Règle à calcul',
    pitch:
      'Deux réglettes graduées en ln : multiplier revient à ajouter des longueurs, ln(ab) = ln a + ln b.',
    minRank: 7,
    maxRank: 7,
    families: ['fonctions'],
    notionOnly: true,
    match: {
      text: /regle a calcul|proprietes algebriques (de|du) (ln|logarithme)|ln\(ab\)|ln a \+ ln b|logarithme decimal|echelles? logarithmiques?/,
      notText: /derivee|limite|croissances? comparees?|integr/,
    },
  },
  {
    id: 'linear-map',
    title: 'Application linéaire du plan',
    pitch:
      'Déplace f(i) et f(j), les colonnes de la matrice : le plan se déforme, l’aire est multipliée par |det M| (det M < 0 : orientation retournée), les droites propres restent en place.',
    minRank: 8,
    maxRank: 9,
    families: ['matrices'],
    notionOnly: true,
    match: {
      text: /applications? lineaires?|determinant|valeurs? propres?|vecteurs? propres?|elements propres|sous espaces? propres?|polynome caracteristique|diagonalis|trigonalis|endomorphisme|noyau|theoreme du rang|rang d une matrice|projecteurs?|symetries? (orthogonales?|vectorielles?)|isometries? du plan|matrices orthogonales|sous espaces stables|matrices semblables|theoreme spectral|\btrace (d une |d un |de la )?(matrice|endomorphisme)|\btrace et (ses proprietes|determinant)|rotations? du plan|matrices? de rotation/,
      notDomain:
        /structures|differentiel|topologie|equations? differentielles?|graphes?|probab|optique|physique|nucleaire|courbes?/,
      notText:
        /vandermonde|blocs|cycles|signature|operations elementaires|developpement selon|pivot|adjoint|gram|lemme des noyaux|polynome annulateur|changement de base|espaces? vectoriels? *$|^espaces? vectoriels?|de l espace|dans l espace|courbe|continuite|positives|morphismes? de groupes|morphismes? d anneaux|colinearite/,
    },
  },
  {
    id: 'redox',
    title: 'Oxydoréduction',
    pitch:
      'Demi-équations électroniques pas à pas (élément, O, H, charge), équation de la réaction (les électrons s’éliminent) et relation à l’équivalence d’un titrage.',
    minRank: 6,
    maxRank: 7,
    families: ['chimie'],
    notionOnly: true,
    discipline: 'physique-chimie',
    match: {
      text: /oxydo|redox|\boxydant|\breducteur|demi equations? electroniques?|couples? ox|titrage/,
      domain: /oxydo|redox/,
      // un titrage acide-base (pH-métrie, conductimétrie) n'est pas une oxydoréduction
      notText: /acide base|acido basique|ph metri|\bph\b|conductimetri/,
    },
  },
  {
    id: 'circuit',
    title: 'Circuit électrique',
    pitch:
      'Ferme l’interrupteur : le courant circule et les lampes brillent. Série ou dérivation, lampe dévissée ou court-circuitée, mesures de U et de I, lois des circuits, loi d’Ohm, puissance.',
    minRank: 1,
    maxRank: 6,
    families: ['physique'],
    notionOnly: true,
    discipline: 'physique-chimie',
    match: {
      text: /circuits?|en serie|en derivation|court circuit|lois? d ohm|dipoles?|conducteurs? ohmiques?|resistance d un dipole|lois? des (?:n(?:oe|œ)uds|mailles)|mesurer une tension|intensite et debit de charges|puissance electrique|energie electrique|effet joule|bilan de puissance|sources? reelles? de tension|point de fonctionnement|quelles lampes|puissance nominale/,
      // condensateur, RC (Tle) ; son (intensité sonore, timbre) ; transferts thermiques
      notText: /condensateur|\brc\b|sonore|timbre|hauteur|thermique|courant variable|conversions?/,
    },
  },
  {
    id: 'motion',
    title: 'Mouvement et énergie',
    pitch:
      'Lance le film : voiture qui roule, démarre ou freine, manège, balle lâchée ou lancée. Chronophotographie, référentiel, vecteurs vitesse, Δv⃗ et accélération, énergies cinétique et de position, distance d’arrêt.',
    minRank: 1,
    maxRank: 7,
    families: ['physique'],
    notionOnly: true,
    discipline: 'physique-chimie',
    match: {
      // pas « mouvement » seul : c'est le nom du thème du programme (« Mouvement et interactions »)
      text: /decrire un mouvement|mouvement depend|sens d un mouvement|relativite du mouvement|mouvements? (?:rectilignes?|circulaires?|uniformes?|acceleres?|ralentis?|curvilignes?)|trajectoires?|chronophotographie|referentiel|calculer une vitesse|comparer des vitesses|vitesse (?:constante|moyenne|qui varie)|convertir une vitesse|vecteurs? (?:vitesse|acceleration|position)|variation (?:du vecteur )?(?:de )?vitesse|energie cinetique|energie de position|energie mecanique|energie potentielle de pesanteur|conservation de l energie|distance d arret|travail (?:d une force|du poids)|principe d inertie|forces? non compensees|chute libre|acceleration|repere de frenet|uniformement accelere/,
      // lumière, son, ondes (célérité) ; cinétique chimique ; astres et particules (autres outils)
      notText:
        /lumiere|\bson\b|sonore|ondes?\b|celerite|vitesse de reaction|demi reaction|volumique|ordre 1|kepler|satellite|orbite|particule|champ electrique|rotation|revolution|photon|combustion|electrique|circuit|thermique|fluide/,
    },
  },
  {
    id: 'waves',
    title: 'Ondes et son',
    pitch:
      'Un clap qui se propage, un écho, une onde le long d’un axe : retard, longueur d’onde. Le son à l’oscilloscope (hauteur, timbre, décibels), la diffraction, les fentes d’Young et l’effet Doppler.',
    minRank: 3,
    maxRank: 7,
    families: ['physique'],
    notionOnly: true,
    discipline: 'physique-chimie',
    match: {
      text: /\bsons?\b|sonores?|audition|audible|infrasons?|ultrasons?|echo|orage|timbre|decibels?|vibreur|resonance|ondes? mecaniques?|celerite|retard|double periodicite|longueur d onde|seismes?|diffraction|interferences?|fentes? d young|interfrange|doppler/,
      // la lumière (vitesse, couleurs, images, photons) et l'électricité ont leurs outils
      notText:
        /lumiere|photon|couleur|lentille|image|spectre|dispersion|reseau|absorbance|atome|niveaux d energie|capteur|condensateur|circuit|loi d ohm|lunette|puissances de dix/,
    },
  },
  {
    id: 'optics',
    title: 'Banc d’optique',
    pitch:
      'Lampe, objet et écran : ombres et propagation rectiligne. Réfraction et réflexion totale (Snell-Descartes), image par une lentille convergente (grandissement, conjugaison), lunette afocale.',
    minRank: 2,
    maxRank: 7,
    families: ['physique'],
    notionOnly: true,
    discipline: 'physique-chimie',
    match: {
      text: /propagation rectiligne|\bombres?\b|conditions pour voir|source primaire|objet diffusant|snell|refraction|indice optique|lentilles?|construire l image|grandissement|modele de l (?:oe|œ)il|conjugaison|lunettes?|grossissement/,
      // le son, les spectres, les couleurs et les photons ont leurs outils
      notText:
        /\bson\b|sonore|reseau|spectres?|photons?|couleurs?|atome|saisons|jour et de la nuit/,
    },
  },
  {
    id: 'atom',
    title: 'Atome et noyau',
    pitch:
      'Construis un atome : protons, neutrons, électrons, ions et isotopes ; écriture ᴬ_Z X, configuration électronique, place dans le tableau. Puis fais se désintégrer un noyau (α, β⁻, β⁺).',
    minRank: 4,
    maxRank: 7,
    families: ['chimie'],
    notionOnly: true,
    discipline: 'physique-chimie',
    match: {
      text: /constitution de l atome|former un ion|atome et noyau|numero atomique|ecriture conventionnelle|configuration electronique|electrons de valence|valence et tableau|tableau periodique|charge d un ion|ion monoatomique|reaction nucleaire|desintegrations?|isotopes?/,
      // la loi de décroissance, la liaison et les molécules ont leurs outils
      notText: /decroissance|demi vie|\bactivite\b|lewis|molecules?|liaisons?|test/,
    },
  },
  {
    id: 'ph',
    title: 'pH et acidité',
    pitch:
      'Place des solutions sur l’échelle de pH (papier pH, ions H⁺ et HO⁻), dilue un acide ou une base : le pH tend vers 7. En Tle : [H₃O⁺], K_e, diagramme de prédominance et indicateur coloré.',
    minRank: 4,
    maxRank: 7,
    families: ['chimie'],
    notionOnly: true,
    discipline: 'physique-chimie',
    match: {
      text: /echelle de ph|\bph\b|ions hydrogene|acide ou basique|acide et une base|acide avec un metal|acide base|potentiel hydrogene|oxonium|predominance|force d un acide|solution tampon|alpha amines/,
      // le titrage et l'oxydoréduction ont leurs outils
      notText: /titrage|oxydoreduction|demi equation/,
    },
  },
  {
    id: 'matter',
    title: 'États de la matière',
    pitch:
      'Chauffe ou refroidis de l’eau, du cyclohexane, de l’acide stéarique ou de l’eau salée : courbe de température, paliers, particules, masse et volume. Puis la masse volumique (flotter ou couler) et la dissolution jusqu’à saturation.',
    minRank: 1,
    maxRank: 5,
    families: ['chimie'],
    notionOnly: true,
    discipline: 'physique-chimie',
    match: {
      text: /changements? d etat|modele particulaire|masse conservee|ebullition|evaporation|corps pur|solubilite|dissolution et saturation|masse volumique|meme volume|flotter|calculer une masse ou un volume|endothermique|identifier une espece chimique|concentration en masse/,
      // la composition de l'air, le dosage, la séparation et les gaz dissous : autres notions
      notText: /composition|dosage|etalonnage|un gaz dans l eau|gaz parfait|separ/,
    },
  },
  {
    id: 'gas',
    title: 'Gaz et pression',
    pitch:
      'Une seringue d’air et son manomètre : loi de Mariotte en 1re, gaz parfait P × V = n × R × T en Tle (isotherme, isochore, isobare, kelvins). Et un plongeur qui descend : P = P_atm + ρ g h.',
    minRank: 6,
    maxRank: 7,
    families: ['physique'],
    notionOnly: true,
    discipline: 'physique-chimie',
    match: {
      text: /mariotte|\bpression|statique des fluides|decrivent un fluide|gaz parfait|evolution d un gaz|temperature absolue|masse volumique d un gaz/,
      // Archimède et Venturi : autres notions de mécanique des fluides
      notText: /venturi|archimede/,
    },
  },
  {
    id: 'colors',
    title: 'Couleurs et absorbance',
    pitch:
      'Projecteurs rouge, vert, bleu (synthèse additive), lampe, filtres et objets colorés (synthèse soustractive), puis une solution au spectrophotomètre : spectre, couleur complémentaire, A = ε × ℓ × c et dosage.',
    minRank: 6,
    maxRank: 7,
    families: ['physique'],
    notionOnly: true,
    discipline: 'physique-chimie',
    match: {
      text: /syntheses? (additive|soustractive)|couleur percue|vision des couleurs|beer lambert|absorbance/,
    },
  },
  {
    id: 'rc',
    title: 'Circuit RC',
    pitch:
      'Charge et décharge d’un condensateur : le film de u_C(t) et i(t), τ = R × C lu à 63 % ou 37 %, la tangente à l’origine, le régime permanent après 5τ.',
    minRank: 7,
    maxRank: 7,
    families: ['physique'],
    notionOnly: true,
    discipline: 'physique-chimie',
    match: {
      text: /condensateur|\brc\b|courant variable|constante de temps/,
      notText: /electrostatique/,
    },
  },
  {
    id: 'law-sim',
    title: 'Simulateur de lois',
    pitch:
      'Monty Hall : garder ou changer de porte ? Selon le niveau, la loi géométrique et son absence de mémoire, la loi de Poisson, limite de la binomiale.',
    minRank: 6,
    maxRank: 9,
    families: ['probabilites'],
    notionOnly: true,
    match: {
      text: /monty hall|lois? geometriques?|absence de memoire|temps d attente|premier succes|lois? de poisson|processus de poisson|lois usuelles/,
      notDomain: /chimie|physique|suite|fonction|differentiel/,
      // lois à densité (exponentielle : elle aussi « sans mémoire ») : pas cet outil
      notText: /exponentielle|densite|continue|normale/,
    },
  },
  {
    id: 'reaction-progress',
    title: 'Tableau d’avancement',
    pitch:
      'Fais avancer la réaction : le tableau d’avancement se remplit, les réactifs s’épuisent, le réactif limitant fixe x_max et l’état final (en Tle : x_f et τ).',
    minRank: 5,
    maxRank: 7,
    families: ['chimie'],
    notionOnly: true,
    discipline: 'physique-chimie',
    match: {
      // « stœchiométrique » garde son œ après normalisation
      text: /avancement|reactifs? limitants?|reactifs? en exces|etat final|st(?:oe|œ)chiometri|transformations? (non )?totales?/,
      domain: /avancement/,
      notDomain: /suite|fonction|probab|statist|algebre/,
      // même bloc du programme de 2nde, autre notion
      notText:
        /endotherm|exotherm|ajuster|equilibrer|synthese|nombres? st(?:oe|œ)chiometriques|transformation physique/,
    },
  },
  {
    id: 'gram-schmidt',
    title: 'Gram-Schmidt et projection',
    pitch:
      'Une famille de ℝ³ orthonormalisée étape par étape (on retire les projections, on normalise), puis le projeté orthogonal sur une droite ou un plan, la distance et les coordonnées en base orthonormée.',
    minRank: 8,
    maxRank: 9,
    families: ['matrices', 'vecteurs'],
    notionOnly: true,
    match: {
      text: /gram schmidt|orthonormalis|projections? orthogonales?|projetes? orthogonal|bases? orthonormees?|familles? orthonormees?|distance a un sous espace|espaces? prehilbertiens?|espaces? euclidiens?|supplementaires? orthogonal|orthogonal d un sous espace/,
      notDomain: /probab|physique|chimie|differentiel|integr/,
      // une matrice de Gram, des polynômes orthogonaux : pas cet outil
      notText: /matrices? de gram|polynomes? orthogonaux|matrices? orthogonales?|series de fourier/,
    },
  },
  {
    id: 'lagrange',
    title: 'Interpolation de Lagrange',
    pitch:
      'Déplace des points : le polynôme interpolateur passe par eux, la base Lᵢ vaut 1 en xᵢ et 0 ailleurs ; puis interpoler une fonction et voir le phénomène de Runge.',
    minRank: 8,
    maxRank: 9,
    families: ['algebre', 'fonctions'],
    notionOnly: true,
    match: {
      text: /interpolat|polynomes? de lagrange|base de lagrange|runge/,
      // le théorème de Lagrange sur les groupes, les multiplicateurs : pas cet outil
      notText: /groupe|theoreme de lagrange|multiplicateur/,
    },
  },
  {
    id: 'level-gradient',
    title: 'Lignes de niveau et gradient',
    pitch:
      'Déplace un point A sur les lignes de niveau de f(x, y) : ∇f(A) est orthogonal à la ligne de niveau et pointe vers les valeurs croissantes ; puis trouver les points critiques et leur nature.',
    minRank: 8,
    maxRank: 9,
    families: ['fonctions'],
    notionOnly: true,
    match: {
      text: /gradient|lignes? de niveau|courbes? de niveau|derivees? partielles?|derivee directionnelle|points? critiques?|condition necessaire d extremum|courbes? implicites?|hessienne/,
      // physique (point critique d'un diagramme (P, T), gradient de température…)
      notDomain: /thermo|diffusion|mecanique|electr|optique|physique/,
      // plan tangent à une surface, extrema liés, EDP : pas cet outil plan
      notText: /sous contrainte|plan tangent|point triple|aux derivees partielles/,
    },
  },
  {
    id: 'param-curve',
    title: 'Courbe paramétrée',
    pitch:
      'Fais avancer t : M(t) parcourt la courbe, f′(t) dirige la tangente ; repère de Frenet, courbure, cercle osculateur, longueur, et l’allure aux points singuliers.',
    minRank: 8,
    maxRank: 9,
    families: ['fonctions', 'geometrie'],
    notionOnly: true,
    match: {
      text: /courbes? parametree|arcs? parametres?|points? singuliers?|rebroussement|repere de frenet|courbure|abscisse curviligne|longueur d un arc|cercle osculateur|cycloide|astroide|cardioide/,
      // physique (optique, mécanique), surfaces et courbes de l'espace, points
      // singuliers d'une équation différentielle : pas cet outil plan
      notDomain: /thermo|diffusion|mecanique|electr|optique|physique|equations? differentielles?/,
      notText:
        /equations? differentielles?|systemes? differentiels?|surface|plan tangent|lentille|miroir|dioptre|de l espace/,
    },
  },
  {
    id: 'space-frame',
    title: 'Repère de l’espace',
    pitch:
      'Points, vecteurs et plans dans un repère 3D qu’on fait tourner : longueur et milieu, colinéarité et coplanarité, équation de plan, droite paramétrée, intersection, projeté et distance.',
    minRank: 7,
    maxRank: 9,
    families: ['espace', 'vecteurs'],
    notionOnly: true,
    match: {
      domain: /geometrie dans l espace/,
      text: /representation parametrique d une droite|equations? cartesiennes? d un plan|equations? de plans?|plans? et de droites de l espace|plans? de l espace|droites? de l espace|vecteurs? de l espace|coplanar|reperes? de l espace|reperage dans l espace|coordonnees dans l espace|distance d un point a un plan|vecteurs? normal a un plan|equations? normales? d un plan|projete orthogonal d un point sur un plan|position relative d une droite et d un plan|intersection d une droite et d un plan|orthogonalite dans l espace/,
      // surfaces (plan tangent), algèbre linéaire ou bilinéaire, physique : pas cet outil
      notDomain:
        /bilineaire|lineaire|prehilbert|euclidien|differentielle|physique|mecanique|cinematique|electr|optique|onde|thermo/,
      notText: /surface|tangent/,
    },
  },
];

export function toolById(id: string): ToolDef | null {
  return TOOLS.find((t) => t.id === id) ?? null;
}

/** minuscules, sans accents, apostrophes et tirets en espaces */
function norm(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[’'\-/&]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Mots-clés → famille, dans l'ordre : le PREMIER qui correspond gagne. Les
 * libellés composés passent avant les mots courts qu'ils contiennent
 * (« calcul integral » avant « calcul », « nombres complexes » avant
 * « nombres », « algebre lineaire » avant « algebre »).
 */
const DOMAIN_RULES: ReadonlyArray<readonly [string, ToolFamily]> = [
  // avant « transformation » : « Transformations chimiques », « Constitution et
  // transformations de la matière » (thème du BO) sont de la chimie
  ['oxydo', 'chimie'],
  ['redox', 'chimie'],
  ['chimi', 'chimie'],
  ['matiere', 'chimie'],
  ['titrage', 'chimie'],
  ['avancement', 'chimie'],
  ['acide', 'chimie'],
  ['synthese organique', 'chimie'],
  ['dosage', 'chimie'],
  // thèmes du programme de physique (6e → Tle) : outils de physique-chimie
  ['energie', 'physique'],
  ['mouvement', 'physique'],
  ['interactions', 'physique'],
  ['ondes', 'physique'],
  ['signaux', 'physique'],
  ['univers', 'physique'],
  ['electri', 'physique'],
  ['optique', 'physique'],
  ['trigo', 'trigonometrie'],
  ['complexe', 'complexes'],
  ['matrice', 'matrices'],
  ['graphe', 'matrices'],
  ['algebre lineaire', 'matrices'],
  ['algebre bilineaire', 'matrices'],
  ['integr', 'integrales'],
  ['primitive', 'integrales'],
  ['equations differentielles', 'integrales'],
  ['calcul litteral', 'algebre'],
  ['calcul algebrique', 'algebre'],
  ['pensee algebrique', 'algebre'],
  ['calcul differentiel', 'fonctions'],
  ['geometrie dans l espace', 'espace'],
  ['geometrie differentielle', 'fonctions'],
  ['espace', 'espace'],
  ['produit scalaire', 'vecteurs'],
  ['vecteur', 'vecteurs'],
  ['transformation', 'transformations'],
  ['geometrie', 'geometrie'],
  ['reperage', 'geometrie'],
  ['droite', 'geometrie'],
  ['denombrement', 'probabilites'],
  ['probabilit', 'probabilites'],
  ['statistique', 'statistiques'],
  ['donnees', 'statistiques'],
  ['suite', 'suites'],
  ['serie', 'suites'],
  ['fonction', 'fonctions'],
  ['derivation', 'fonctions'],
  ['second degre', 'fonctions'],
  ['exponentielle', 'fonctions'],
  ['logarithme', 'fonctions'],
  ['limite', 'fonctions'],
  ['convexite', 'fonctions'],
  ['analyse', 'fonctions'],
  ['polynome', 'algebre'],
  ['structures', 'algebre'],
  ['inequation', 'algebre'],
  ['equation', 'algebre'],
  ['algebre', 'algebre'],
  ['fraction', 'fractions'],
  ['arithmetique', 'arithmetique'],
  ['proportionnalite', 'proportionnalite'],
  ['information chiffree', 'proportionnalite'],
  ['grandeur', 'grandeurs'],
  ['algorithm', 'algorithmique'],
  ['pensee informatique', 'algorithmique'],
  ['logique', 'algorithmique'],
  ['raisonnement', 'algorithmique'],
  ['puissance', 'calcul'],
  ['racine', 'calcul'],
  ['relatif', 'calcul'],
  ['nombre', 'calcul'],
  ['calcul', 'calcul'],
];

/** Famille d'un `domain_label` de SKILLCARD, ou null si rien ne correspond. */
export function familyOfDomain(label: string | null | undefined): ToolFamily | null {
  if (!label) return null;
  const n = norm(label);
  if (!n) return null;
  for (const [key, family] of DOMAIN_RULES) if (n.includes(key)) return family;
  return null;
}

/**
 * Indices plus fins tirés du titre ou du sujet de la carte : une carte de
 * « Géométrie » intitulée « Cosinus dans le triangle rectangle » relève aussi
 * de la trigonométrie.
 */
const HINT_RULES: ReadonlyArray<readonly [RegExp, ToolFamily]> = [
  [/\b(cos|sin|tan|cosinus|sinus|tangente|trigo|radian|cercle trigonometrique)/, 'trigonometrie'],
  [/\b(multipli|tables? de multiplication|produit|facteur|diviseur|multiple)/, 'calcul'],
  [/\b(courbe|graphique|parabole|affine|fonctions? lineaires?|f\s?\()/, 'fonctions'],
];

export function familiesOfHints(texts: ReadonlyArray<string | null | undefined>): ToolFamily[] {
  const out = new Set<ToolFamily>();
  for (const t of texts) {
    if (!t) continue;
    const n = norm(t);
    for (const [re, family] of HINT_RULES) if (re.test(n)) out.add(family);
  }
  return [...out];
}

/** L'outil sert-il à ce niveau ? Niveau inconnu : oui. */
export function toolFitsLevel(tool: ToolDef, level: string | null | undefined): boolean {
  const rank = schoolLevelRank(level);
  if (rank === null) return true;
  return rank >= tool.minRank && rank <= tool.maxRank;
}

export interface ToolboxContext {
  /** niveau de l'exercice (chapitre, sinon exercice) */
  level: string | null;
  /** familles de la question (domaines de ses SKILLCARDS + indices) */
  families: readonly ToolFamily[];
  /** ses SKILLCARDS (domaine, sujet, titre), pour les règles `match` des outils */
  cards?: readonly CardTexts[];
  /**
   * Outils reliés EXPLICITEMENT à la SKILLCARD (table `card_tools`, choisis
   * par l'auteur de la carte) : ils passent avant toute règle automatique,
   * même hors du niveau (l'auteur a tranché).
   */
  linked?: readonly string[];
}

export interface ToolboxSelection {
  /** outils du domaine de la question, au bon niveau */
  featured: ToolDef[];
  /** autres outils du niveau */
  others: ToolDef[];
}

/**
 * Outils proposés pour une question. « Pour cette question » : les outils que
 * la carte désigne par ses mots (Pythagore, Thalès…), sinon ceux de son
 * domaine. Puis les autres outils du niveau, ceux du domaine en tête. Un outil
 * qui ne sert pas à ce niveau n'apparaît pas. Ordre du catalogue.
 */
export function toolsForContext(ctx: ToolboxContext): ToolboxSelection {
  // un outil d'une autre matière n'apparaît que dans le contexte de sa famille
  const fit = TOOLS.filter(
    (t) =>
      toolFitsLevel(t, ctx.level) &&
      (!t.discipline || t.families.some((f) => ctx.families.includes(f))),
  );
  const linked = linkedTools(ctx.linked);
  if (linked.length > 0) {
    return { featured: linked, others: fit.filter((t) => !linked.includes(t)) };
  }
  const wanted = new Set(ctx.families);
  const cards = ctx.cards ?? [];
  const named = fit.filter((t) => cards.some((c) => cardMatchesTool(t, c)));
  const ofFamily = fit.filter((t) => t.families.some((f) => wanted.has(f)));
  const featured = named.length > 0 ? named : ofFamily.filter((t) => !t.notionOnly);
  const others = [
    ...ofFamily.filter((t) => !featured.includes(t)),
    ...fit.filter((t) => !featured.includes(t) && !ofFamily.includes(t)),
  ];
  return { featured, others };
}

/** SKILLCARD de chimie (domaine) ? */
export function isChemistryCard(card: Pick<CardTexts, 'domain_label'>): boolean {
  return familyOfDomain(card.domain_label) === 'chimie';
}

/** SKILLCARD de physique-chimie (chimie ou physique, d'après son domaine) ? */
export function isPhysicsChemistryCard(card: Pick<CardTexts, 'domain_label'>): boolean {
  const f = familyOfDomain(card.domain_label);
  return f === 'chimie' || f === 'physique';
}

/**
 * L'outil est-il de la matière de la carte ? Une carte de physique-chimie n'appelle
 * que les outils de physique-chimie, une carte de maths que ceux de maths.
 */
export function toolSuitsCard(tool: ToolDef, card: Pick<CardTexts, 'domain_label'>): boolean {
  return isPhysicsChemistryCard(card) === !!tool.discipline;
}

/** La SKILLCARD désigne-t-elle cet outil ? (règle `match`, sans regarder le niveau) */
export function cardMatchesTool(tool: ToolDef, card: CardTexts): boolean {
  const m = tool.match;
  if (!m) return false;
  if (!toolSuitsCard(tool, card)) return false;
  const domain = card.domain_label ? norm(card.domain_label) : '';
  if (m.notDomain && domain && m.notDomain.test(domain)) return false;
  if (m.notText && [card.title, card.topic].some((t) => !!t && m.notText!.test(norm(t))))
    return false;
  if (m.domain && domain && m.domain.test(domain)) return true;
  if (!m.text) return false;
  const re = m.text;
  return [card.title, card.topic].some((t) => !!t && re.test(norm(t)));
}

/** Familles d'une question à partir de ses SKILLCARDS (domaine, sujet, titre). */
export function familiesOfCards(
  cards: ReadonlyArray<{
    domain_label?: string | null;
    topic?: string | null;
    title?: string | null;
  }>,
): ToolFamily[] {
  const out = new Set<ToolFamily>();
  for (const c of cards) {
    const f = familyOfDomain(c.domain_label);
    if (f) out.add(f);
    for (const h of familiesOfHints([c.topic, c.title])) out.add(h);
  }
  return [...out];
}

/**
 * Outils qu'une SKILLCARD désigne (règle `match`) et qui servent à l'un de
 * ses niveaux : suggestion de liens `card_tools` (pré-remplissage, bouton
 * « Suggérer » de l'éditeur). Les familles de domaine, trop larges pour un
 * lien explicite, n'entrent pas ici.
 */
export function suggestedToolsForCard(
  card: CardTexts & { levels?: ReadonlyArray<string> | null },
): ToolId[] {
  const levels = card.levels && card.levels.length > 0 ? card.levels : [null];
  return TOOLS.filter(
    (t) => cardMatchesTool(t, card) && levels.some((l) => toolFitsLevel(t, l)),
  ).map((t) => t.id);
}

/** Identifiant d'outil connu du catalogue (les liens en base sont du texte libre). */
export function isToolId(id: string): id is ToolId {
  return (TOOL_IDS as readonly string[]).includes(id);
}

/**
 * Outils reliés (`card_tools`) → définitions, dans l'ordre de l'auteur, sans
 * doublon ; un identifiant inconnu (outil retiré du code) est ignoré.
 */
export function linkedTools(ids: readonly string[] | null | undefined): ToolDef[] {
  const out: ToolDef[] = [];
  for (const id of ids ?? []) {
    const t = toolById(id);
    if (t && !out.includes(t)) out.push(t);
  }
  return out;
}

/** Une SKILLCARD telle que la boîte la lit : ses textes et ses outils reliés. */
export interface ToolboxCard extends CardTexts {
  tools?: readonly string[] | null;
}

/**
 * Contexte de la boîte pour une ou plusieurs SKILLCARDS (la question du
 * player peut en avoir plusieurs ; la page d'une carte, une) : familles et
 * règles `match` de toutes, outils reliés de toutes (ordre des cartes puis de
 * l'auteur).
 */
export function contextForCards(
  cards: readonly ToolboxCard[],
  level: string | null,
): ToolboxContext {
  const texts = cards.map(({ domain_label, topic, title }) => ({ domain_label, topic, title }));
  return {
    level,
    families: familiesOfCards(texts),
    cards: texts,
    linked: linkedTools(cards.flatMap((c) => c.tools ?? [])).map((t) => t.id),
  };
}

/**
 * Niveau à donner aux outils ouverts depuis la page d'une carte : celui de
 * l'élève connecté, sinon le plus avancé de la carte (l'outil montre tout ce
 * que la carte couvre).
 */
export function toolLevelForCard(
  cardLevels: readonly string[] | null | undefined,
  studentLevel: string | null | undefined,
): string | null {
  if (studentLevel && schoolLevelRank(studentLevel) !== null) return studentLevel;
  let best: string | null = null;
  for (const l of cardLevels ?? []) {
    const r = schoolLevelRank(l);
    if (r !== null && (best === null || r > (schoolLevelRank(best) ?? -1))) best = l;
  }
  return best;
}
